import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axiosInstance from '../../utils/axios';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { CalendarIcon, ClockIcon, MapPinIcon, UserGroupIcon, TruckIcon, PhoneIcon, EnvelopeIcon } from '@heroicons/react/24/outline';

function MyBookings() {
  const { currentUser } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const response = await axiosInstance.get('/api/bookings/rider');
        console.log('Fetched bookings:', response.data); // For debugging
        setBookings(response.data);
      } catch (err) {
        console.error('Error fetching bookings:', err);
        setError('Failed to load your bookings');
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, []);

  const handleCancelBooking = async (bookingId) => {
    try {
      await axiosInstance.put(`/api/bookings/${bookingId}/cancel`);
      
      // Update the booking status in the UI
      setBookings(prevBookings => 
        prevBookings.map(booking => 
          booking.id === bookingId 
            ? { ...booking, status: 'cancelled' } 
            : booking
        )
      );
    } catch (err) {
      console.error('Error cancelling booking:', err);
      setError('Failed to cancel booking');
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'confirmed':
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <div className="text-center py-10">Loading your bookings...</div>;
  }

  if (error) {
    return <div className="text-center py-10 text-red-600">{error}</div>;
  }

  if (bookings.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">My Bookings</h1>
        <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6 text-center">
          <p className="text-gray-500">You don't have any bookings yet.</p>
          <button
            onClick={() => window.location.href = '/rider/book-ride'}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Book a Ride
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">My Bookings</h1>
      
      <div className="space-y-6">
        {bookings.map(booking => (
          <div key={booking.id} className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {booking.rideId ? 'Ride Booking' : 'Church Ride Request'}
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Booking #{booking.id}
                </p>
              </div>
              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(booking.status)}`}>
                {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
              </span>
            </div>
            
            <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                {/* Pickup & Destination Section */}
                <div className="sm:col-span-2 bg-gray-50 p-4 rounded-lg mb-2">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Trip Details</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <MapPinIcon className="h-5 w-5 mr-1 text-gray-400" />
                        Pickup Location
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {booking.pickupStreet && booking.pickupCity && booking.pickupPostcode 
                          ? (
                            <div>
                              <div>{booking.pickupStreet}</div>
                              <div>{booking.pickupCity} {booking.pickupPostcode}</div>
                            </div>
                          )
                          : booking.pickupLocation
                        }
                      </dd>
                    </div>
                    
                    <div>
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <MapPinIcon className="h-5 w-5 mr-1 text-gray-400" />
                        Destination
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {booking.destination || booking.Ride?.dropoffLocation || 'Church'}
                      </dd>
                    </div>
                  </div>
                </div>
                
                {/* Time Section */}
                <div className="sm:col-span-2 bg-gray-50 p-4 rounded-lg mb-2">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Schedule</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <CalendarIcon className="h-5 w-5 mr-1 text-gray-400" />
                        Date
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {formatDate(booking.date || booking.Ride?.date)}
                      </dd>
                    </div>
                    
                    <div>
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <ClockIcon className="h-5 w-5 mr-1 text-gray-400" />
                        {booking.rideId ? 'Departure Time' : 'Service Time'}
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {booking.serviceTime || booking.Ride?.time || 
                         formatDateTime(booking.Ride?.departureTime) || 'N/A'}
                      </dd>
                    </div>
                  </div>
                </div>

                {/* Driver Details - Only shown if confirmed or accepted */}
                {((booking.Ride?.driver && (booking.status === 'confirmed' || booking.status === 'accepted')) ||
                  (booking.driver && booking.status === 'accepted')) && (
                  <div className="sm:col-span-2 bg-gray-50 p-4 rounded-lg mb-2">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Driver Information</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Driver</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {booking.driver 
                            ? `${booking.driver.firstName} ${booking.driver.lastName}`
                            : `${booking.Ride.driver.firstName} ${booking.Ride.driver.lastName}`
                          }
                        </dd>
                      </div>
                      
                      {/* Phone number with fallback to dummy number */}
                      <div>
                        <dt className="text-sm font-medium text-gray-500 flex items-center">
                          <PhoneIcon className="h-4 w-4 mr-1 text-gray-400" />
                          Phone
                        </dt>
                        <dd className="mt-1 text-sm flex items-center">
                          <a 
                            href={`tel:${(booking.driver?.phone || booking.Ride?.driver?.phone) || '555-123-4567'}`} 
                            className="text-indigo-600 hover:text-indigo-800 flex items-center"
                          >
                            {(booking.driver?.phone || booking.Ride?.driver?.phone) || '555-123-4567'}
                            <span className="ml-2 text-xs bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full">
                              Call
                            </span>
                          </a>
                        </dd>
                        <p className="text-xs text-gray-500 mt-1">Click to call driver directly</p>
                      </div>
                      
                      {(booking.driver?.email || booking.Ride?.driver?.email) && (
                        <div className="sm:col-span-2">
                          <dt className="text-sm font-medium text-gray-500 flex items-center">
                            <EnvelopeIcon className="h-4 w-4 mr-1 text-gray-400" />
                            Email
                          </dt>
                          <dd className="mt-1 text-sm">
                            <a 
                              href={`mailto:${booking.driver?.email || booking.Ride?.driver?.email}`} 
                              className="text-indigo-600 hover:text-indigo-800"
                            >
                              {booking.driver?.email || booking.Ride?.driver?.email}
                            </a>
                          </dd>
                        </div>
                      )}
                      
                      {booking.Ride?.vehicle && (
                        <div className="sm:col-span-2 mt-3">
                          <dt className="text-sm font-medium text-gray-500 flex items-center">
                            <TruckIcon className="h-4 w-4 mr-1 text-gray-400" />
                            Vehicle
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {booking.Ride.vehicle.make} {booking.Ride.vehicle.model} ({booking.Ride.vehicle.color}, {booking.Ride.vehicle.licensePlate})
                          </dd>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Pending Request Notice - Only shown for pending status */}
                {booking.status === 'pending' && (
                  <div className="sm:col-span-2 bg-yellow-50 p-4 rounded-lg mb-2 border border-yellow-200">
                    <h4 className="text-sm font-medium text-yellow-800 mb-2">Request Pending</h4>
                    <p className="text-sm text-yellow-700">
                      Your ride request is waiting for a driver to accept it. Driver information will be available once your request is confirmed.
                    </p>
                  </div>
                )}

                {/* Passenger Details */}
                <div>
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <UserGroupIcon className="h-5 w-5 mr-1 text-gray-400" />
                    {booking.rideId ? 'Seats' : 'Passengers'}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">{booking.seats || booking.passengers || 1}</dd>
                </div>
                
                {/* Service Details */}
                {booking.serviceSession && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Service</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {booking.serviceSession} Service
                    </dd>
                  </div>
                )}
                
                {/* Special Requests */}
                {booking.specialRequests && (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">Special Requests</dt>
                    <dd className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded">
                      {booking.specialRequests}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
            
            <div className="border-t border-gray-200 px-4 py-4 sm:px-6">
              {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                <button
                  onClick={() => handleCancelBooking(booking.id)}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Cancel Booking
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default MyBookings; 